import {
  FC,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  startTransition
} from 'react';

import { useTranslations } from 'hooks/useTranslations';
import { ExpandIcon } from 'components/UI/ExpandIcon';

import { Container, DescriptionText, ToggleButton } from './styled';

type Props = {
  children?: string;
  collapsedHeight?: number;
};

export const ProductDescription: FC<Props> = ({
  children,
  collapsedHeight = 70
}) => {
  const t = useTranslations();
  // State
  const [isExpanded, setIsExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState(collapsedHeight);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Refs
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    setIsOverflowing(el.scrollHeight > collapsedHeight);
  }, [children, collapsedHeight]);

  // Expanded
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    if (isExpanded) {
      // Lock to current height, then expand to scrollHeight
      el.style.maxHeight = `${el.clientHeight}px`;
      requestAnimationFrame(() => {
        el.style.maxHeight = `${el.scrollHeight}px`;
        setMaxHeight(el.scrollHeight);
      });
      return;
    }
    // Collapse back to the initial height
    el.style.maxHeight = `${collapsedHeight}px`;
    startTransition(() => {
      setMaxHeight(collapsedHeight);
    });
    return;
  }, [isExpanded, collapsedHeight]);

  // Keep in sync if content changes while expanded
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !isExpanded) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      el.style.maxHeight = `${el.scrollHeight}px`;
      setMaxHeight(el.scrollHeight);
    });

    resizeObserver.observe(el);
    return () => {
      resizeObserver.disconnect();
    };
  }, [isExpanded]);

  // Toggle the expansion state
  const onToggle = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  if (!children) {
    return null;
  }

  return (
    <Container>
      <DescriptionText
        ref={ref}
        $maxHeight={maxHeight}
        aria-expanded={isExpanded}
      >
        {children}
      </DescriptionText>
      {isOverflowing && (
        <ToggleButton
          onClick={onToggle}
          type="button"
          aria-expanded={isExpanded}
        >
          <ExpandIcon isOpen={isExpanded} size={10} />
          {t(isExpanded ? 'button.read_less' : 'button.read_more')}
        </ToggleButton>
      )}
    </Container>
  );
};
